import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Ruler } from "lucide-react";

const MEASUREMENTS = [
  {
    label: "Lens Width",
    code: "First number",
    desc: "The width of one lens in millimetres. This is the measurement that most affects how wide a frame looks on your face — a few millimetres either side is usually fine, more than five is noticeable.",
  },
  {
    label: "Bridge Width",
    code: "Second number",
    desc: "The gap between the two lenses, sitting across your nose. Match this to frames you already wear and a new pair will sit where you expect it to.",
  },
  {
    label: "Temple Length",
    code: "Third number",
    desc: "The arm length, measured from the hinge to the tip that sits behind your ear. Most frames fall between 135mm and 150mm.",
  },
];

export default function FitGuide() {
  return (
    <div data-testid="fit-guide-page">
      <section className="bg-charcoal py-16 sm:py-24">
        <div className="container-lookers max-w-3xl">
          <div className="flex items-center gap-2 mb-3">
            <Ruler size={18} className="text-teal-400" strokeWidth={2} />
            <span className="text-teal-400 text-xs font-heading font-bold uppercase tracking-[0.2em]">Preloved Without The Guesswork</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mb-5">Find Your Fit</h1>
          <p className="text-white/70 text-sm sm:text-base">
            Every Lookers listing shows the real measurements of that exact frame — not a generic size chart. The quickest way to know whether a frame will suit you is to read the numbers printed inside the arm of glasses you already own.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="container-lookers max-w-3xl">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl text-neutral-900 tracking-tight mb-3">The Three Numbers Inside Your Arm</h2>
          <p className="text-sm text-neutral-500 mb-8">
            Open a pair you wear often and look along the inside of the temple. You'll find something like <span className="font-mono font-bold text-neutral-900">52 □ 18 140</span> — lens width, bridge width and temple length, all in millimetres.
          </p>
          <div className="divide-y divide-neutral-200 border-y border-neutral-200">
            {MEASUREMENTS.map((m, i) => (
              <div key={m.label} className="py-6" data-testid={`fit-guide-measurement-${i}`}>
                <div className="flex items-baseline justify-between gap-4 mb-2">
                  <h3 className="font-heading font-extrabold text-lg text-neutral-900">{m.label}</h3>
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-teal-700 shrink-0">{m.code}</span>
                </div>
                <p className="text-sm text-neutral-500 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-[#F8F8F9] rounded-3xl p-7 sm:p-9">
            <h2 className="font-heading font-extrabold text-xl text-neutral-900 mb-3">Still Not Sure? See Them On</h2>
            <p className="text-sm text-neutral-500 mb-6">
              Measurements tell you how a frame will sit. Our try-on shows you how it will look — the frames are tracked to your face and sized from their real measurements, whether you use your camera or a selfie.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/try-on" className="inline-flex items-center gap-2 bg-charcoal text-white font-heading font-bold text-sm uppercase tracking-wide px-7 py-3.5 rounded-full hover:bg-teal-600 transition-all hover:scale-[1.02] active:scale-95" data-testid="fit-guide-tryon-link">
                Try Frames On <ArrowUpRight size={16} />
              </Link>
              <Link to="/account/sizes" className="inline-flex items-center gap-2 border border-neutral-300 text-charcoal font-heading font-bold text-sm uppercase tracking-wide px-7 py-3.5 rounded-full hover:border-charcoal transition-colors" data-testid="fit-guide-sizes-link">
                Save Your Size
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
